import { ArrowLeft } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { ApiError } from '@/lib/api/errors'
import { kuniriIconWeight } from '@/lib/icons'

import { ActivityForm } from '../activity-form'
import { activityComposerApi } from '../api'
import type { ActivityDetail, Category } from '../types'

type EditState =
  | { status: 'loading' }
  | { status: 'ready'; activity: ActivityDetail; categories: Category[] }
  | { status: 'unavailable' }
  | { status: 'forbidden' }
  | { status: 'locked' }
  | { status: 'started' }
  | { status: 'error'; error: unknown }

const EDITABLE_STATUSES = new Set(['draft', 'published', 'full'])

interface EditActivityViewProps {
  activityId: string
  onRetry: () => void
}

function EditBlocked({
  titleKey,
  bodyKey,
  activityId,
}: {
  titleKey: string
  bodyKey: string
  activityId: string | null
}) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className="mt-6 mx-auto flex max-w-xl flex-col items-center gap-4 rounded-md border border-line bg-white p-6 text-center"
    >
      <div className="space-y-2">
        <h2 className="text-2xl">{t(titleKey)}</h2>
        <p className="text-muted">{t(bodyKey)}</p>
      </div>
      <Link
        to={activityId ? `/activity/${activityId}` : '/home'}
        className="btn-secondary min-h-11 px-4 text-sm"
      >
        {activityId ? t('activityForm.edit.backToActivity') : t('common.back')}
      </Link>
    </div>
  )
}

/**
 * `/activity-edit/:id` (UI_PARITY §3.10). The form is only shown to the creator,
 * before start, while the activity is draft/published/full. Every other case
 * gets explicit copy instead of a silently disabled editor.
 */
export function ActivityEditScreen() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [reloadKey, setReloadKey] = useState(0)
  const activityId = id?.trim() ? id.trim() : null

  return (
    <section aria-labelledby="edit-title" className="content-width pt-[var(--safe-top)] pb-10">
      <div className="flex flex-wrap items-center gap-2 py-4">
        <Link
          to={activityId ? `/activity/${activityId}` : '/home'}
          className="btn-ghost min-h-11 px-2 text-sm"
        >
          <ArrowLeft
            size={19}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {activityId
            ? t('activityForm.edit.backToActivity')
            : t('common.back')}
        </Link>
      </div>

      <h1 id="edit-title" className="text-[clamp(2rem,4vw,3rem)]">
        {t('screens.activityEdit.title')}
      </h1>

      {activityId ? (
        <EditActivityView
          key={`${activityId}:${reloadKey}`}
          activityId={activityId}
          onRetry={() => setReloadKey((value) => value + 1)}
        />
      ) : (
        <EditBlocked
          titleKey="activityForm.edit.missingTitle"
          bodyKey="activityForm.edit.missingBody"
          activityId={null}
        />
      )}
    </section>
  )
}

function EditActivityView({ activityId, onRetry }: EditActivityViewProps) {
  const [state, setState] = useState<EditState>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      activityComposerApi.getActivity(activityId, {
        signal: controller.signal,
      }),
      activityComposerApi.listCategories({ signal: controller.signal }),
    ])
      .then(([activity, categories]) => {
        if (controller.signal.aborted) return
        if (activity.viewerStatus !== 'creator') {
          setState({ status: 'forbidden' })
          return
        }
        if (!EDITABLE_STATUSES.has(activity.status)) {
          setState({ status: 'locked' })
          return
        }
        const start = activity.startAt ? Date.parse(activity.startAt) : Number.NaN
        if (!Number.isFinite(start) || start <= Date.now()) {
          setState({ status: 'started' })
          return
        }
        setState({ status: 'ready', activity, categories })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'unavailable' })
          return
        }
        if (error instanceof ApiError && error.status === 403) {
          setState({ status: 'forbidden' })
          return
        }
        setState({ status: 'error', error })
      })
    return () => controller.abort()
  }, [activityId])

  if (state.status === 'loading') {
    return <LoadingState variant="form" labelKey="activityForm.edit.loading" />
  }

  if (state.status === 'unavailable') {
    return (
      <EditBlocked
        titleKey="activityForm.edit.unavailableTitle"
        bodyKey="activityForm.edit.unavailableBody"
        activityId={null}
      />
    )
  }

  if (state.status === 'forbidden') {
    return (
      <EditBlocked
        titleKey="activityForm.edit.forbiddenTitle"
        bodyKey="activityForm.edit.forbiddenBody"
        activityId={activityId}
      />
    )
  }

  if (state.status === 'locked') {
    return (
      <EditBlocked
        titleKey="activityForm.edit.lockedTitle"
        bodyKey="activityForm.edit.lockedBody"
        activityId={activityId}
      />
    )
  }

  if (state.status === 'started') {
    return (
      <EditBlocked
        titleKey="activityForm.edit.startedTitle"
        bodyKey="activityForm.edit.startedBody"
        activityId={activityId}
      />
    )
  }

  if (state.status === 'error') {
    return (
      <div className="mt-6">
        <ErrorState
          error={state.error}
          titleKey="activityForm.edit.loadErrorTitle"
          onRetry={onRetry}
        />
      </div>
    )
  }

  if (state.categories.length === 0) {
    return (
      <div className="mt-6">
        <ErrorState
          titleKey="activityForm.errors.categoriesTitle"
          bodyKey="activityForm.errors.categories"
          onRetry={onRetry}
        />
      </div>
    )
  }

  return (
    <div className="mt-5">
      <ActivityForm
        mode="edit"
        activity={state.activity}
        categories={state.categories}
      />
    </div>
  )
}
